import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import logException from '../services/logException';
import { createAuthStore } from '../context/AuthContext/helpers';
import { tracking } from '@dashboard/lib/tracking';

/**
 * Hook to switch stores with cache clearing
 *
 * Usage:
 * const { switchStore, isSwitching } = useSwitchStore();
 *
 * switchStore(sellerId);
 * createStore();
 */
export const useSwitchStore = () => {
  const {
    switchStore: switchStoreAuth,
    isSwitchingStore,
    updateAuthContext,
    token,
    availableStores,
  } = useAuth();
  const queryClient = useQueryClient();
  const history = useHistory();

  const switchStore = async sellerId => {
    try {
      await switchStoreAuth(sellerId, {
        navigate: path => history.replace(path),
        onError: error => {
          logException({ message: 'Failed to switch store', error });
        },
      });
    } catch {
      // Error already handled in onError callback
    }
  };

  const createStore = async ({ displayName } = {}) => {
    try {
      const storeCountBefore = availableStores?.length || 0;
      const response = await createAuthStore(token, { displayName });
      const { user, seller } = response.data;

      tracking.trackEvent('store_created', {
        store_id: seller._id,
        is_first_store: storeCountBefore === 0,
        store_count: storeCountBefore + 1,
      });

      updateAuthContext({ user, seller });

      // Clear and invalidate all queries to refresh data for the new store
      queryClient.clear();
      queryClient.invalidateQueries();

      // Navigate to onboarding form for new store with query param
      history.push('/seller/start?dashboard_create_store=true');
    } catch (error) {
      logException({ message: 'Failed to create store', error });
      throw error;
    }
  };

  return {
    switchStore,
    createStore,
    isSwitching: isSwitchingStore,
  };
};
